import { c as _c } from "react/compiler-runtime";
import { useTimezone } from "@/hooks/use-timezone";
import { CALENDAR_TITLE_WRAP_CLASSNAME, segmentHeaderClassName, useCalendarTextSize } from "@/lib/calendar-text-size";
import { cx } from "@/lib/tailwind-utils";
import { EventPrepSegmentTiny } from "@/types/event";
import { SEGMENT_TITLE_STYLE } from "./calendar-event/appointment-service-segment-card";
import { CalendarTimeRange } from "./calendar-event/calendar-time-range";

/** Default color for prep time segments when no color is specified */
export const PREP_TIME_DEFAULT_COLOR = "#94A3B8";

/** Default prep time duration in minutes when enabling prep time */
export const DEFAULT_PREP_TIME_DURATION_MINUTES = 15;

/** Default buffer time in minutes between prep end and service start */
export const DEFAULT_PREP_TIME_BUFFER_MINUTES = 0;
interface PrepTimeSliceProps {
  prepTime: EventPrepSegmentTiny;
  /** Start percentage (0-100) from top of container */
  topPct: number;
  /** End percentage (0-100) from bottom of container */
  bottomPct: number;
  /** Additional styles to apply (for calendar event integration) */
  style?: React.CSSProperties;
  /** Optional class name override */
  className?: string;
  /** Patient name to display after prep label */
  patientName?: string;
}

/**
 * Renders a prep time segment on the calendar.
 * Used in both appointment-wrapper (calendar view) and services-slices-list (preview).
 */
export function PrepTimeSlice(t0) {
  const $ = _c(49);
  const {
    prepTime,
    topPct,
    bottomPct,
    style,
    className,
    patientName
  } = t0;
  const timeZone = useTimezone();
  const textSize = useCalendarTextSize();
  let color;
  let prepEnd;
  let prepStart;
  let t1;
  let t2;
  if ($[0] !== prepTime.color || $[1] !== prepTime.end || $[2] !== prepTime.start) {
    prepStart = new Date(prepTime.start);
    if ($[8] !== prepTime.color || $[9] !== prepTime.end) {
      prepEnd = new Date(prepTime.end);
      color = prepTime.color ?? PREP_TIME_DEFAULT_COLOR;
      t1 = prepEnd.getTime();
      $[8] = prepTime.color;
      $[9] = prepTime.end;
      $[10] = color;
      $[11] = prepEnd;
      $[12] = t1;
    } else {
      color = $[10];
      prepEnd = $[11];
      t1 = $[12];
    }
    t2 = prepStart.getTime();
    $[0] = prepTime.color;
    $[1] = prepTime.end;
    $[2] = prepTime.start;
    $[3] = color;
    $[4] = prepEnd;
    $[5] = prepStart;
    $[6] = t1;
    $[7] = t2;
  } else {
    color = $[3];
    prepEnd = $[4];
    prepStart = $[5];
    t1 = $[6];
    t2 = $[7];
  }
  const prepDurationMinutes = (t1 - t2) / 60000;
  const t3 = prepDurationMinutes <= 15 ? "overflow-hidden whitespace-nowrap" : prepDurationMinutes <= 20 ? "line-clamp-2 break-words" : "break-words";
  let t4;
  if ($[13] !== className || $[14] !== t3) {
    t4 = cx("pointer-events-auto absolute left-0 right-0 overflow-hidden rounded-md [container-type:size]", t3, className);
    $[13] = className;
    $[14] = t3;
    $[15] = t4;
  } else {
    t4 = $[15];
  }
  const t5 = `${topPct}%`;
  const t6 = `${bottomPct}%`;
  const t7 = `${color}50`;
  const t8 = `${color}40`;
  let t9;
  if ($[16] !== style || $[17] !== t5 || $[18] !== t6 || $[19] !== t7 || $[20] !== t8) {
    t9 = {
      top: t5,
      bottom: t6,
      borderWidth: "1px",
      backgroundColor: t7,
      borderColor: t8,
      ...style
    };
    $[16] = style;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  let t10;
  if ($[22] !== color) {
    t10 = {
      borderLeftColor: color
    };
    $[22] = color;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== textSize) {
    t11 = segmentHeaderClassName(textSize);
    $[24] = textSize;
    $[25] = t11;
  } else {
    t11 = $[25];
  }
  let t12;
  if ($[26] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = cx("overflow-hidden text-calendar-tiny font-medium leading-tight", CALENDAR_TITLE_WRAP_CLASSNAME);
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  const t13 = prepTime.title ?? "Prep";
  let t14;
  if ($[27] !== t13) {
    t14 = <span>{t13}</span>;
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  const t15 = patientName && ` - ${patientName}`;
  let t16;
  if ($[29] !== t15) {
    t16 = <span>{t15}</span>;
    $[29] = t15;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== t14 || $[32] !== t16) {
    t17 = <div className={t12} style={SEGMENT_TITLE_STYLE}>{t14}{t16}</div>;
    $[31] = t14;
    $[32] = t16;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== prepEnd || $[35] !== prepStart || $[36] !== timeZone) {
    t18 = <div className="max-w-full truncate text-calendar-micro opacity-80"><CalendarTimeRange start={prepStart} end={prepEnd} timeZone={timeZone} /></div>;
    $[34] = prepEnd;
    $[35] = prepStart;
    $[36] = timeZone;
    $[37] = t18;
  } else {
    t18 = $[37];
  }
  let t19;
  if ($[38] !== t11 || $[39] !== t17 || $[40] !== t18) {
    t19 = <div className="ml-1 min-w-0 flex-1"><div className={t11}>{t17}{t18}</div></div>;
    $[38] = t11;
    $[39] = t17;
    $[40] = t18;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== t10 || $[43] !== t19) {
    t20 = <div className="flex h-full rounded-l-md border-l-[3px]" style={t10}>{t19}</div>;
    $[42] = t10;
    $[43] = t19;
    $[44] = t20;
  } else {
    t20 = $[44];
  }
  let t21;
  if ($[45] !== t20 || $[46] !== t4 || $[47] !== t9) {
    t21 = <div className={t4} style={t9}>{t20}</div>;
    $[45] = t20;
    $[46] = t4;
    $[47] = t9;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  return t21;
}